import type { Icon } from '@onefootprint/icons';
import { IcoClose16 } from '@onefootprint/icons';
import * as RadixDialog from '@radix-ui/react-dialog';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '../../icon-button';

import { HEADER_HEIGHT } from '../dialog.constants';

type ActionConfig = {
  component: Icon;
  ariaLabel: string;
  onClick: () => void;
};

interface HeaderProps {
  title: string;
  onClose: () => void;
  icon?: {
    component: Icon;
    ariaLabel: string;
    onClick?: () => void;
  };
  action?: ActionConfig;
  secondaryAction?: ActionConfig;
}

const DialogHeader = forwardRef<HTMLDivElement, HeaderProps>(
  ({ title, onClose, icon, action, secondaryAction }, ref) => {
    const { t } = useTranslation('ui');
    const IconComponent: Icon = icon?.component ?? IcoClose16;
    // @ts-ignore: fails for dashboard project due to i18next selector
    const iconAriaLabel = icon?.ariaLabel ?? t('components.dialog.header-icon.aria-label-default');
    const iconOnClick = icon?.onClick ?? onClose;
    const ActionIcon = action?.component;
    const SecondaryActionIcon = secondaryAction?.component;

    return (
      <header
        className="grid grid-cols-[24px_1fr_auto] items-center sticky px-2 top-0 z-10 bg-primary border-b border-tertiary border-solid rounded-t shrink-0"
        style={{
          height: `${HEADER_HEIGHT}px`,
        }}
        ref={ref}
      >
        {/* Not RadixDialog.Close: it merges a dismiss handler onto the caller's icon, so a back arrow
            would run its action and close the dialog. */}
        <IconButton aria-label={iconAriaLabel} onClick={iconOnClick} icon={IconComponent} size="tiny" />
        <RadixDialog.Title asChild>
          <h3 className="text-center truncate text-label-2 text-primary">{title}</h3>
        </RadixDialog.Title>
        <div className="flex items-center gap-1 min-w-6 justify-end">
          {SecondaryActionIcon && secondaryAction && (
            <IconButton
              aria-label={secondaryAction.ariaLabel}
              onClick={secondaryAction.onClick}
              icon={SecondaryActionIcon}
              size="tiny"
            />
          )}
          {ActionIcon && action && (
            <IconButton aria-label={action.ariaLabel} onClick={action.onClick} icon={ActionIcon} size="tiny" />
          )}
        </div>
      </header>
    );
  },
);

DialogHeader.displayName = 'DialogHeader';

export default DialogHeader;
